import {
  CallInitialsLandmark,
  callerPrimaryLabel,
  Badge,
  formatRelativeTimeIso,
  cn,
} from '@yeshuman/sdk'
import type { CallRecordListItem } from '@yeshuman/sdk'
import { Link } from 'react-router-dom'

import { petsSubtitle } from './pets-subtitle'
import type { CallOutcome } from '../hooks/use-call-outcomes'
import { formatAppointmentStartAt } from '../lib/appointment-windows'

function badgeVariantForOutcome(status: string) {
  if (status === 'booked' || status === 'completed') return 'successSoft' as const
  if (status === 'cancelled') return 'destructive' as const
  if (status === 'booking_requested') return 'warningSoft' as const
  return 'secondary' as const
}

function badgeVariantForSync(syncStatus: string) {
  if (syncStatus === 'dirty') return 'warningSoft' as const
  if (syncStatus === 'mocked') return 'secondary' as const
  return null
}

export type CallOutcomeRowProps = {
  outcome: CallOutcome
  /** Matching call record for landmark / pets when available */
  row?: CallRecordListItem | null
  className?: string
}

export function CallOutcomeRow({ outcome, row, className }: CallOutcomeRowProps) {
  const booking = outcome.latest_booking
  const identityRow = row ?? {
    customer_name: outcome.contact_name || outcome.patient_name || '',
    customer_number: '',
    call_state: 'routine',
  }
  const title =
    outcome.patient_name?.trim() ||
    outcome.contact_name?.trim() ||
    callerPrimaryLabel(identityRow)
  const subtitle = row ? petsSubtitle(row) : null
  const appointmentLine = booking
    ? `${formatAppointmentStartAt(booking.start_at)} · ${booking.duration_minutes} min`
    : null
  const typeResource =
    booking &&
    [booking.appointment_type_name, booking.resource_name]
      .map((s) => s?.trim())
      .filter(Boolean)
      .join(' · ')
  const preview = typeResource || outcome.notes?.trim() || null
  const syncVariant = booking ? badgeVariantForSync(booking.sync_status) : null

  return (
    <Link
      to={`/calls/records/${outcome.call_record_id}`}
      className={cn(
        'flex items-start gap-3 rounded-lg border bg-card p-3 text-sm transition-colors hover:bg-muted/50',
        className
      )}
    >
      <CallInitialsLandmark row={identityRow} size="row" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold leading-tight truncate">{title}</div>
            {subtitle ? (
              <div className="text-xs text-muted-foreground truncate mt-0.5">{subtitle}</div>
            ) : outcome.contact_name && outcome.patient_name ? (
              <div className="text-xs text-muted-foreground truncate mt-0.5">
                {outcome.contact_name}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5 shrink-0">
            <Badge variant={badgeVariantForOutcome(outcome.outcome_status)} className="font-normal">
              {outcome.outcome_status}
            </Badge>
            {syncVariant && booking ? (
              <Badge variant={syncVariant} className="font-normal">
                {booking.sync_status}
              </Badge>
            ) : null}
          </div>
        </div>
        {appointmentLine ? (
          <p className="text-sm font-medium text-foreground tabular-nums">{appointmentLine}</p>
        ) : null}
        {preview ? <p className="text-xs text-muted-foreground line-clamp-2">{preview}</p> : null}
        <p className="text-xs text-muted-foreground">
          Updated {formatRelativeTimeIso(outcome.updated_at)}
        </p>
      </div>
    </Link>
  )
}
